import { useState, useEffect } from 'react'
import { X, Share } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// beforeinstallprompt は React マウント前に発火するためモジュールレベルで保持
let deferredPrompt: BeforeInstallPromptEvent | null = null
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferredPrompt = e as BeforeInstallPromptEvent
})

function getIsStandalone() {
  const standalone = (navigator as { standalone?: boolean }).standalone
  return (
    standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}

function getIsIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

export default function AddToHomeScreenBanner() {
  const isStandalone = getIsStandalone()
  const isIOS = getIsIOS()
  const [dismissed, setDismissed] = useState(false)
  const [canInstall, setCanInstall] = useState(!!deferredPrompt)

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      deferredPrompt = e as BeforeInstallPromptEvent
      setCanInstall(true)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (isStandalone || dismissed) return null
  if (!isIOS && !canInstall) return null

  async function handleInstall() {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    deferredPrompt = null
    setDismissed(true)
  }

  function handleDismiss() {
    setDismissed(true)
  }

  return (
    <div className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm">
      {isIOS ? (
        <span className="flex-1">
          <Share size={12} className="inline mb-0.5" /> を押して「ホームに追加」するとアプリとして使えます
        </span>
      ) : (
        <>
          <span className="flex-1">アプリとしてホームに追加できます</span>
          <button
            onClick={handleInstall}
            className="shrink-0 px-3 py-1 rounded bg-white/20 hover:bg-white/30 transition-colors font-medium min-h-[44px]"
          >
            追加
          </button>
        </>
      )}
      <button
        onClick={handleDismiss}
        className="shrink-0 p-1 rounded-full hover:bg-white/20 transition-colors"
        aria-label="閉じる"
      >
        <X size={14} />
      </button>
    </div>
  )
}
